import { useState, useEffect } from 'react';
import axios from 'axios';
import { FiUsers, FiLayers, FiActivity, FiAward } from 'react-icons/fi';

interface Stats {
  totalUsers: number;
  activeUsers24h: number;
  totalLevels: number;
  avgCompletedLevel: number;
  totalProgress: number;
  completionRate: string;
}

const AdminStats = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-stats">Loading statistics...</div>;
  }

  if (!stats) {
    return <div className="error">Failed to load statistics</div>;
  }

  return (
    <div className="admin-stats">
      <h2>Dashboard Overview</h2>

      <div className="stats-grid">
        <div className="stat-card-large card">
          <div className="stat-icon-large">
            <FiUsers />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.totalUsers}</div>
            <div className="stat-title">Total Users</div>
            <div className="stat-subtitle">{stats.activeUsers24h} active in last 24h</div>
          </div>
        </div>

        <div className="stat-card-large card">
          <div className="stat-icon-large levels">
            <FiLayers />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.totalLevels}</div>
            <div className="stat-title">Total Levels</div>
            <div className="stat-subtitle">Active levels</div>
          </div>
        </div>

        <div className="stat-card-large card">
          <div className="stat-icon-large activity">
            <FiActivity />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.avgCompletedLevel}</div>
            <div className="stat-title">Avg. Level Reached</div>
            <div className="stat-subtitle">Across all users</div>
          </div>
        </div>

        <div className="stat-card-large card">
          <div className="stat-icon-large award">
            <FiAward />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.completionRate}</div>
            <div className="stat-title">Completion Rate</div>
            <div className="stat-subtitle">{stats.totalProgress} total attempts</div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <button className="btn btn-primary" onClick={fetchStats}>
          Refresh Statistics
        </button>
      </div>

      <style>{`
        .admin-stats {
          max-width: 1200px;
        }
        .admin-stats h2 {
          margin-bottom: var(--spacing-lg);
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: var(--spacing-lg);
          margin-bottom: var(--spacing-xl);
        }
        .stat-card-large {
          display: flex;
          align-items: center;
          gap: var(--spacing-lg);
          padding: var(--spacing-xl);
        }
        .stat-icon-large {
          width: 70px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gradient-primary);
          border-radius: var(--radius-lg);
          font-size: 2rem;
          flex-shrink: 0;
        }
        .stat-icon-large.levels {
          background: var(--gradient-success);
        }
        .stat-icon-large.activity {
          background: linear-gradient(135deg, var(--neon-yellow) 0%, var(--neon-orange) 100%);
        }
        .stat-icon-large.award {
          background: var(--gradient-warning);
        }
        .stat-content {
          flex: 1;
        }
        .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          line-height: 1;
          margin-bottom: var(--spacing-xs);
        }
        .stat-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-xs);
        }
        .stat-subtitle {
          font-size: 0.875rem;
          color: var(--text-muted);
        }
        .quick-actions {
          display: flex;
          gap: var(--spacing-md);
        }
        .loading-stats,
        .error {
          padding: var(--spacing-xl);
          text-align: center;
          color: var(--text-secondary);
        }
        @media (max-width: 768px) {
          .stat-card-large {
            flex-direction: column;
            text-align: center;
          }
          .stat-number {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminStats;
